import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { countryCode, CountryList } from "../../apis/api/CountryList";
import { IsCompanyExist } from "../../apis/api/MemberApi";
import { unwrapResult } from "@reduxjs/toolkit";
import './BranchModal.css';

const rs = (err) => ({
  control: (b, s) => ({ ...b, borderRadius: '11px', minHeight: '42px',
    border: `1.5px solid ${err ? '#ef4444' : s.isFocused ? '#586AD9' : '#e2e8f0'}`,
    boxShadow: s.isFocused ? '0 0 0 3px rgba(88,106,217,.08)' : 'none',
    background: '#fafbfc', fontSize: '0.85rem',
    '&:hover': { borderColor: err ? '#ef4444' : '#586AD9' } }),
  option: (b, s) => ({ ...b, fontSize: '0.84rem',
    background: s.isSelected ? '#586AD9' : s.isFocused ? '#eef0fb' : '#fff',
    color: s.isSelected ? '#fff' : '#1e293b' }),
  menuPortal: b => ({ ...b, zIndex: 99999 }),
  menu: b => ({ ...b, borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.16)', zIndex: 99999 }),
  menuList: b => ({
    ...b,
    maxHeight: '200px',
    padding: '6px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': { width: '5px' },
    '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '4px' }
  })
});

export default function EditBranchModal({ show, onHide, onSubmit, branchData, oldEmail }) {
  const dispatch = useDispatch();
  const overlayRef = React.useRef(null);
  const countryCodesData = useSelector((state) => state.countrySlice.countryCode);
  const countryListData = useSelector((state) => state.countrySlice.country);
  const [codes, setCodes] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => { dispatch(countryCode()); dispatch(CountryList()); }, [dispatch]);
  useEffect(() => { if (countryCodesData?.data?.data) setCodes(countryCodesData.data.data); }, [countryCodesData]);
  useEffect(() => { if (Array.isArray(countryListData)) setCountries(countryListData); }, [countryListData]);

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm();

  useEffect(() => {
    if (branchData && show) {
      reset({
        address: branchData.address || '',
        house_number: branchData.house_number || '',
        cityName: branchData.city || '',
        countryName: branchData.country || '',
        pin_code: branchData.pin_code || '',
        email: branchData.email || '',
        phoneCode: branchData.country_code ? { value: branchData.country_code, label: branchData.country_code } : null,
        phoneNumber: branchData.contact || '',
      });
    }
  }, [branchData, show, reset]);

  useEffect(() => {
    if (!show) return;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.setProperty('overflow', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');

    const overlayEl = overlayRef.current;

    const handleWheel = (e) => {
      const isInsideSelectMenu = e.target.closest('[class*="brm-select"]') || e.target.closest('[class*="-menu"]') || e.target.closest('[class*="MenuList"]');
      if (isInsideSelectMenu) return;

      const isInsideBody = e.target.closest('.brm-body');
      if (!isInsideBody) {
        e.preventDefault();
        return;
      }
      const bodyEl = isInsideBody;
      const scrollTop = bodyEl.scrollTop;
      const scrollHeight = bodyEl.scrollHeight;
      const height = bodyEl.clientHeight;
      const delta = e.deltaY;

      if (delta < 0 && scrollTop <= 0) {
        e.preventDefault();
      } else if (delta > 0 && scrollTop + height >= scrollHeight - 1) {
        e.preventDefault();
      }
    };

    const handleTouchMove = (e) => {
      const isInsideSelectMenu = e.target.closest('[class*="brm-select"]') || e.target.closest('[class*="-menu"]') || e.target.closest('[class*="MenuList"]');
      if (isInsideSelectMenu) return;

      const isInsideBody = e.target.closest('.brm-body');
      if (!isInsideBody) {
        e.preventDefault();
      }
    };

    if (overlayEl) {
      overlayEl.addEventListener('wheel', handleWheel, { passive: false });
      overlayEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    }

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      if (overlayEl) {
        overlayEl.removeEventListener('wheel', handleWheel);
        overlayEl.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [show]);

  const submitForm = (data) => { onSubmit(data); onHide(); };

  if (!show) return null;

  return (
    <div className="brm-overlay" ref={overlayRef} onClick={onHide}>
      <div className="brm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="brm-header">
          <div className="brm-header-left">
            <div className="brm-header-icon" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>
              <i className="bx bx-edit-alt" style={{ color: '#d97706' }}></i>
            </div>
            <div>
              <h3>Edit Branch</h3>
              <p>Update details for {branchData?.city || 'this'} office</p>
            </div>
          </div>
          <button className="brm-close" onClick={onHide}><i className="bx bx-x"></i></button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit(submitForm)}>
          <div className="brm-body">
            <div className="brm-section-title"><i className="bx bx-map"></i> Location</div>

            <div className="brm-row">
              <div className="brm-col-6 brm-field">
                <label className="brm-label">Country <span>*</span></label>
                <Controller name="countryName" control={control} rules={{ required: "Required" }}
                  render={({ field }) => (
                    <Select value={countries.find(c => c.label === field.value) || (field.value ? { label: field.value, value: field.value } : null)}
                      onChange={opt => field.onChange(opt?.label || '')} options={countries}
                      placeholder="Select country" styles={rs(!!errors.countryName)} menuPortalTarget={document.body}
                      menuPosition="fixed" menuPlacement="auto" classNamePrefix="brm-select" maxMenuHeight={180} />
                  )} />
                {errors.countryName && <div className="brm-error"><i className="bx bx-error-circle"></i>{errors.countryName.message}</div>}
              </div>
              <div className="brm-col-6 brm-field">
                <label className="brm-label">City <span>*</span></label>
                <input className="brm-input" placeholder="e.g. Dubai" {...register("cityName", { required: "Required" })} />
                {errors.cityName && <div className="brm-error"><i className="bx bx-error-circle"></i>{errors.cityName.message}</div>}
              </div>
            </div>

            <div className="brm-row">
              <div className="brm-col-8 brm-field">
                <label className="brm-label">Street Address <span>*</span></label>
                <input className="brm-input" placeholder="e.g. Business Bay, Tower A" {...register("address", { required: "Required" })} />
                {errors.address && <div className="brm-error"><i className="bx bx-error-circle"></i>{errors.address.message}</div>}
              </div>
              <div className="brm-col-4 brm-field">
                <label className="brm-label">House / Unit</label>
                <input className="brm-input" placeholder="e.g. 12A" {...register("house_number")} />
              </div>
            </div>

            <div className="brm-field" style={{ maxWidth: '200px' }}>
              <label className="brm-label">Pin / Zip Code</label>
              <input className="brm-input" placeholder="e.g. 500001" {...register("pin_code")} />
            </div>

            <div className="brm-section-title" style={{ marginTop: '8px' }}><i className="bx bx-phone"></i> Contact</div>

            <div className="brm-field">
              <label className="brm-label">Email <span>*</span></label>
              <input className="brm-input" type="email" placeholder="branch@company.com"
                {...register("email", {
                  required: "Required",
                  pattern: { value: /^\S+@\S+$/i, message: "Invalid email" },
                  validate: async (value) => {
                    if (value === oldEmail) return true;
                    try { const a = await dispatch(IsCompanyExist({ company_email: value })); const r = unwrapResult(a); if (r.status === "true") return r.message; return true; } catch { return true; }
                  },
                })} />
              {errors.email && <div className="brm-error"><i className="bx bx-error-circle"></i>{errors.email.message}</div>}
            </div>

            <div className="brm-field">
              <label className="brm-label">Phone Number <span>*</span></label>
              <div className="brm-phone-row">
                <div className="brm-phone-code">
                  <Controller name="phoneCode" control={control} rules={{ required: "Required" }}
                    render={({ field }) => (
                      <Select {...field} options={codes} placeholder="+code"
                        getOptionLabel={e => e.label || e.value} getOptionValue={e => e.value}
                        styles={rs(!!errors.phoneCode)} menuPortalTarget={document.body}
                        menuPosition="fixed" menuPlacement="top" classNamePrefix="brm-select" maxMenuHeight={180} />
                    )} />
                </div>
                <div className="brm-phone-number">
                  <input className="brm-input" type="tel" placeholder="4 123 4567"
                    {...register("phoneNumber", { required: "Required", pattern: { value: /^[0-9]{7,15}$/, message: "Invalid" } })} />
                </div>
              </div>
              {(errors.phoneCode || errors.phoneNumber) && (
                <div className="brm-error"><i className="bx bx-error-circle"></i>{errors.phoneCode?.message || errors.phoneNumber?.message}</div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="brm-footer">
            <button type="button" className="brm-btn-cancel" onClick={onHide}>Cancel</button>
            <button type="submit" className="brm-btn-submit"><i className="bx bx-check"></i> Update Branch</button>
          </div>
        </form>
      </div>
    </div>
  );
}
